import React, { useState } from 'react';
import Icon from './Icon';

const AFFILIATE_LINK = 'https://shopify.pxf.io/1sar';
// Define the icon URL as a constant for easy updates.
const SHOPIFY_ICON_URL = 'https://enfitah.com/wp-content/uploads/2025/11/shopify-ic-scaled.png';

const AffiliateBanner: React.FC = () => {
  const [isMinimized, setIsMinimized] = useState(false);

  // Stop propagation to prevent the banner's main click handler from firing
  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMinimized(true);
    // As requested, open the affiliate link when the close button is clicked.
    window.open(AFFILIATE_LINK, '_blank', 'noopener,noreferrer');
  };

  const handleExpand = () => {
    setIsMinimized(false);
  };
  
  const handleBannerClick = () => {
    window.open(AFFILIATE_LINK, '_blank', 'noopener,noreferrer');
  };

  const bannerStyle = {
    backgroundImage: `
      linear-gradient(rgba(107, 114, 128, 0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(107, 114, 128, 0.08) 1px, transparent 1px),
      linear-gradient(to right, #e0f5dc, #d4f4cc)
    `,
    backgroundSize: '16px 16px, 16px 16px, 100% 100%',
  };
  
  const handleClick = () => {
    if (isMinimized) {
      // If the banner is minimized, clicking the icon should expand it.
      handleExpand();
    } else {
      // If the banner is expanded, clicking it should open the link.
      handleBannerClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <>
      <div 
        style={{ fontFamily: "'Cairo', sans-serif" }}
        className={`fixed bottom-5 z-50 transition-all duration-700 ease-in-out ${
          isMinimized
            ? 'left-5' // Position on the bottom-left when minimized.
            : 'left-1/2 -translate-x-1/2 max-w-lg w-[calc(100%-2.5rem)] sm:w-auto'
        }`}
      >
        {/* Add a relative container to position the close button */}
        <div className="relative">
          {/* The close button is now outside the banner, positioned absolutely */}
          <button 
              onClick={handleMinimize} 
              className={`absolute top-1/2 right-full mr-2 -translate-y-1/2 z-10 p-1 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] shadow-md hover:bg-black/10 dark:hover:bg-white/10 transition-all duration-300 ${!isMinimized ? 'opacity-100 scale-100' : 'opacity-0 scale-50 pointer-events-none'}`}
              aria-label="إخفاء البانر"
              tabIndex={isMinimized ? -1 : 0}
          >
              <Icon name="X" className="w-3.5 h-3.5 text-[var(--color-text-body)]" />
          </button>

          <div
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            style={!isMinimized ? bannerStyle : {}} // No background style when minimized.
            className={`relative flex items-center text-[13px] transition-all duration-700 ease-in-out cursor-pointer group overflow-hidden ${
                // When minimized, it's just a container for the icon with no shadow.
                isMinimized 
                  ? 'w-16 h-16 bg-transparent' // Increased size
                  // When expanded, it's the full banner with a shadow.
                  : 'gap-3 rounded-xl border border-green-200/80 py-2 px-2.5 shadow-lg'
            }`}
            aria-label={isMinimized ? 'إظهار بانر العرض' : 'عرض شوبيفاي الخاص'}
          >
            {/* The minimized icon. It now slides up into view and slides down out of view. */}
            <div
              className={`absolute top-0 left-0 transition-all duration-500 ease-in-out transform ${
                  isMinimized 
                  ? 'w-16 h-16 opacity-100 scale-100 translate-y-0' // Increased size
                  : 'w-16 h-16 opacity-0 scale-50 translate-y-full'
              }`}
            >
              <div className="relative w-full h-full overflow-hidden rounded-full">
                <img 
                    src={SHOPIFY_ICON_URL} 
                    alt="Shopify offer" 
                    className="w-full h-full"
                />
              </div>
            </div>
            
            {/* The container for the expanded banner's content. It fades and slides down. */}
            <div className={`flex items-center gap-3 transform transition-all duration-500 ease-in-out ${
                isMinimized ? 'opacity-0 translate-y-full' : 'opacity-100 translate-y-0'
            }`}
            aria-hidden={isMinimized}
            >
                {/* Banner Text */}
                <div className="flex-grow">
                    <p className="text-[#0f3d04] leading-tight font-semibold">
                        استفد من 3 أشهر مجانية من شوبيفاي مقابل 1 دولار شهريًا فقط.
                    </p>
                </div>
                
                {/* Claim Button */}
                <div className="ml-auto flex-shrink-0 px-4 py-2 rounded-lg bg-white/70 border border-green-300 font-bold text-[#0f3d04] transition-transform duration-300 group-hover:scale-105 group-active:scale-95">
                    ابدأ الآن
                </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AffiliateBanner;